import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, Mail, Shield, ArrowRight, Building2, Eye, EyeOff } from 'lucide-react';
import { useSubdomain } from '../contexts/OrganizationContext';

export default function Login({ orgData, subdomain: propSubdomain, orgSlug }) {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const hookSubdomain = useSubdomain();
  const subdomain = propSubdomain || orgSlug || hookSubdomain;
  const [organization, setOrganization] = useState(orgData || null);
  const mfaInputRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mfa_code: '',
  });

  // Fetch organization data if not passed as prop
  useEffect(() => {
    const fetchOrg = async () => {
      if (orgData) {
        setOrganization(orgData);
        return;
      }
      if (subdomain && !organization) {
        try {
          const response = await axios.get(`${API}/resolve-subdomain/${subdomain}`);
          setOrganization(response.data);
        } catch (err) {
          console.log('No organization found for subdomain:', subdomain);
        }
      }
    };
    fetchOrg();
  }, [subdomain, orgData, organization]);

  // Get branding colors
  const primaryColor = organization?.primary_color || '#00A3A1';
  const secondaryColor = organization?.secondary_color || '#E94E89';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        email: formData.email.trim(),
      };
      const response = await axios.post(`${API}/auth/login`, payload);
      login(response.data.access_token, response.data.user);
      toast.success('Welcome back!');
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === "MFA code required") {
        setShowMFA(true);
        toast.info('Please enter your MFA code');
        // autofocus the MFA input on next tick
        setTimeout(() => mfaInputRef.current?.focus(), 50);
      } else if (error.response?.status === 429) {
        toast.error(error.response?.data?.detail || 'Too many attempts. Please wait and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit MFA when 6 digits are entered
  useEffect(() => {
    if (showMFA && /^\d{6}$/.test(formData.mfa_code) && !loading) {
      // Trigger form submission programmatically
      const ev = new Event('submit', { cancelable: true, bubbles: true });
      const form = document.querySelector('form[data-login-form]');
      form?.dispatchEvent(ev);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.mfa_code, showMFA]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-[#E6F7F7]/30 flex items-center justify-center p-4">
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl" 
          style={{ background: `linear-gradient(to bottom right, ${primaryColor}15, ${secondaryColor}15)` }}
        />
        <div 
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl" 
          style={{ background: `linear-gradient(to top right, ${secondaryColor}15, ${primaryColor}15)` }}
        />
      </div>

      <Card className="w-full max-w-md shadow-xl border-slate-200/50 bg-white/80 backdrop-blur-sm relative z-10 overflow-hidden">
        {/* Top Gradient Bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-1" 
          style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}
        />
        
        <CardHeader className="space-y-4 text-center pt-8 pb-6">
          {/* Organization Branding */}
          {organization ? (
            <div className="mx-auto space-y-2">
              {organization.logo_url ? (
                <img 
                  src={organization.logo_url} 
                  alt={`${organization.name} Logo`} 
                  className="h-12 mx-auto object-contain"
                />
              ) : (
                <div 
                  className="w-14 h-14 mx-auto rounded-xl flex items-center justify-center shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                >
                  <Building2 className="h-7 w-7 text-white" />
                </div>
              )}
              <p className="text-sm font-medium" style={{ color: primaryColor }}>{organization.name}</p>
            </div>
          ) : (
            <div className="mx-auto">
              <img 
                src="/simplly_logo.png" 
                alt="Simplly Logo" 
                className="h-16 mx-auto object-contain"
              />
            </div>
          )}
          <div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              {organization ? `Welcome to ${organization.name}` : 'Welcome Back'}
            </CardTitle>
            <CardDescription className="text-slate-500 mt-1">
              Sign in to your account
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          <form onSubmit={handleSubmit} data-login-form className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  autoFocus
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="text-xs font-medium hover:underline"
                  style={{ color: primaryColor }}
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {showMFA && (
              <div className="space-y-2 animate-fade-in">
                <Label htmlFor="mfa_code" className="text-sm font-medium text-slate-700">MFA Code</Label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="mfa_code"
                    ref={mfaInputRef}
                    data-testid="login-mfa-input"
                    inputMode="numeric"
                    placeholder="Enter 6-digit code"
                    value={formData.mfa_code}
                    onChange={(e) => setFormData({ ...formData, mfa_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20 tracking-widest font-mono"
                    maxLength={6}
                    required
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Enter the code from your authenticator app — we'll sign you in automatically.
                </p>
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-11 text-white font-medium shadow-lg transition-all duration-200 group"
              style={{ 
                background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}dd)`,
                boxShadow: `0 10px 25px -5px ${primaryColor}30`
              }}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Sign In
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            {organization ? (
              <p className="text-sm text-slate-600">
                Powered by{' '}
                <a href="https://simplly.com.au" className="text-[#00A3A1] font-medium hover:underline">
                  Simplly
                </a>
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                <Link to="/" className="font-medium hover:underline" style={{ color: primaryColor }}>
                  &larr; Back to main site
                </Link>
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
